import i18n from '../i18n';
import { base64Decode, base64Encode, isBase64 } from '../utils/base64';
import { Assignment } from './Assignment';
import { Book, translateBookForTransport, translateBookFromTransport } from './Book';
import { Company } from './Company';
import { Content } from './Content';
import { CourseSection } from './CourseSection';
import { Edition, translateEditionForTransport, translateEditionFromTransport } from './Edition';
import { Item } from './Item';
import { Person } from './Person';
import { Price, translatePriceForTransport, translatePriceFromTransport } from './Price';
import { ProductBookOutline } from './ProductBookOutline';
import { School } from './School';
import { Term } from './Term';

export enum ProductType {
  COURSEWARE = 'COURSEWARE',
  EBOOK = 'EBOOK',
}

export enum ProductStatus {
  READY_FOR_SALE = 'READY_FOR_SALE',
  ADOPTED = 'ADOPTED',
  LAUNCHED = 'LAUNCHED',
  DEACTIVATED = 'DEACTIVATED',
}

export interface Product extends Item {
  publisherId?: number;
  publisher?: Company;
  bookId?: number;
  book?: Book;
  editionId?: number;
  edition?: Edition;

  name: string;
  type: ProductType;

  publisherName?: string;
  bookTitle?: string;
  editionName?: string;
  revisionNumber?: number;
  lessonPlanName?: string;
  authors?: string[];
  isbn?: string;
  coverImageUrl?: string;

  personId?: number;
  person?: Person;

  assignments?: Assignment[];

  schoolId?: number;
  school?: School;

  termId?: number;
  term?: Term;

  courseSectionId?: number;
  courseSection?: CourseSection;

  hasChapterQuiz?: boolean;
  hasChapterReadingComprehension?: boolean;
  hasChapterStudyTool?: boolean;

  parentProductId?: number;
  parentProduct?: Product;

  priceId?: number;
  price?: Price;

  contents?: Content[];

  status: ProductStatus;

  distributorId?: number;
  distributor?: Company;

  productBookOutlineId?: number;
  productBookOutline?: ProductBookOutline;
  aiEngine?: string;

  version?: string;
}

export const displayProduct = (product?: Product): string => {
  if (!product) return '-';

  const { bookTitle, editionName, revisionNumber } = product;

  return `${bookTitle ?? '-'} - ${editionName} ${i18n.t('edition')} - Rev ${revisionNumber}`;
};

export const translateAdoptionForTransport = (attributes: any): any => {
  return translateProductForTransport(attributes);
};

export const translateAdoptionFromTransport = (attributes: any): any => {
  return translateProductFromTransport(attributes);
};

export const translateProductForTransport = (attributes: any): any => {
  const response = { ...attributes };

  if (attributes.book) {
    response.book = translateBookForTransport(attributes.book);
  }

  if (attributes.edition) {
    response.edition = translateEditionForTransport(attributes.edition);
  }

  if (attributes.parentProduct) {
    response.parentProduct = translateProductForTransport(attributes.parentProduct);
  }

  if (attributes.price) {
    response.price = translatePriceForTransport(attributes.price);
  }

  if (attributes.description) {
    response.description = !isBase64(attributes.description)
      ? base64Encode(attributes.description)
      : attributes.description;
  }

  return response;
};

export const translateProductFromTransport = (attributes: any): any => {
  const response = { ...attributes };

  if (attributes.book) {
    response.book = translateBookFromTransport(attributes.book);
  }

  if (attributes.edition) {
    response.edition = translateEditionFromTransport(attributes.edition);
  }

  if (attributes.parentProduct) {
    response.parentProduct = translateProductFromTransport(attributes.parentProduct);
  }

  if (attributes.price) {
    response.price = translatePriceFromTransport(attributes.price);
  }

  if (attributes.description) {
    response.description = isBase64(attributes.description)
      ? base64Decode(attributes.description)
      : attributes.description;
  }

  return response;
};

export const translateLaunchForTransport = (attributes: any): any => {
  return translateProductForTransport(attributes);
};

export const translateLaunchFromTransport = (attributes: any): any => {
  return translateProductFromTransport(attributes);
};

export const translateCoursewareFromTransport = (attributes: any): any => {
  const { description, book, edition, product } = attributes;

  return {
    ...attributes,
    book: book ? translateBookFromTransport(book) : undefined,
    edition: edition ? translateEditionFromTransport(edition) : undefined,
    product: product ? translateProductFromTransport(product) : undefined,
    description: description && isBase64(description) ? base64Decode(description) : description,
  };
};

export const translateCoursewareForTransport = (attributes: any): any => {
  const { description, product } = attributes;

  return {
    ...attributes,
    book: null,
    edition: null,
    product: product ? translateProductForTransport(product) : undefined,
    description: description && !isBase64(description) ? base64Encode(description) : description,
  };
};

export const isProduct = (item: Product | Content): item is Product =>
  !!item && 'bookTitle' in item && item.bookTitle !== undefined;
